/**
 * Streaming Status Store
 *
 * Manages real-time event streaming status and metrics
 *
 * MIGRATED TO USE GENERATED OPENAPI TYPES
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { components } from '@/types/api'

// Use auto-generated types from OpenAPI spec
type StreamingStatusResponse = components['schemas']['StreamingStatusResponse']
type StreamingHealthResponse = components['schemas']['StreamingHealthResponse']
type StreamingMetricsResponse = components['schemas']['StreamingMetricsResponse']
type StreamingConfig = components['schemas']['StreamingConfig']
type EventMetrics = components['schemas']['EventMetrics']

// Frontend-specific simplified status type
export interface StreamingStatus {
  enabled: boolean
  mode: 'websocket' | 'sse' | 'polling' | 'initializing' | 'unknown'
  status: 'connected' | 'disconnected' | 'connecting' | 'reconnecting' | 'failed' | 'unknown'
  streaming: boolean
  healthy: boolean
}

// Frontend-specific metrics type with additional computed fields
export interface StreamingMetrics {
  totalEventsProcessed: number
  eventsByType: Record<string, number>
  lastEventTime: string | null
  eventsPerMinute: number
  connectionUptime: number
}

export const useStreamingStore = defineStore('streaming', () => {
  // State
  const status = ref<StreamingStatus>({
    enabled: false,
    mode: 'unknown',
    status: 'unknown',
    streaming: false,
    healthy: false
  })

  const metrics = ref<StreamingMetrics>({
    totalEventsProcessed: 0,
    eventsByType: {},
    lastEventTime: null,
    eventsPerMinute: 0,
    connectionUptime: 0
  })

  const config = ref<StreamingConfig>({
    enableEventStreaming: false,
    enableWebsocket: false,
    enableSse: false,
    nodeUrl: ''
  })

  const lastUpdateTime = ref<Date | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Computed
  const isRealtime = computed(() => {
    return status.value.streaming &&
           (status.value.mode === 'websocket' || status.value.mode === 'sse')
  })

  const connectionStatusText = computed(() => {
    if (!status.value.enabled) return 'Disabled (Using Polling)'

    switch (status.value.status) {
      case 'connected':
        return status.value.mode === 'polling'
          ? 'Polling Mode'
          : `Connected (${status.value.mode.toUpperCase()})`
      case 'connecting':
        return 'Connecting...'
      case 'reconnecting':
        return 'Reconnecting...'
      case 'disconnected':
        return 'Disconnected'
      case 'failed':
        return 'Connection Failed'
      default:
        return 'Unknown'
    }
  })

  const connectionStatusColor = computed(() => {
    if (!status.value.enabled || status.value.mode === 'polling') {
      return 'gray'
    }

    switch (status.value.status) {
      case 'connected':
        return status.value.healthy ? 'green' : 'yellow'
      case 'connecting':
      case 'reconnecting':
        return 'yellow'
      case 'disconnected':
      case 'failed':
        return 'red'
      default:
        return 'gray'
    }
  })

  const mostActiveEventTypes = computed(() => {
    const entries = Object.entries(metrics.value.eventsByType)
    return entries
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([type, count]) => ({ type, count }))
  })

  // Actions
  async function fetchStatus() {
    isLoading.value = true
    error.value = null

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:9122/api/v1'
      const response = await fetch(`${apiUrl}/streaming/status`)

      if (!response.ok) {
        throw new Error(`Failed to fetch streaming status: ${response.statusText}`)
      }

      const data = await response.json()

      // Update status
      status.value = {
        enabled: data.enabled || false,
        mode: data.mode || 'unknown',
        status: data.status || 'unknown',
        streaming: data.streaming || false,
        healthy: data.health === 'healthy' || data.healthy || false
      }

      // Update metrics if available
      if (data.events) {
        metrics.value = {
          totalEventsProcessed: data.events.total_processed || 0,
          eventsByType: data.events.by_type || {},
          lastEventTime: data.events.last_event_time || null,
          eventsPerMinute: 0, // Calculate if needed
          connectionUptime: 0 // Calculate if needed
        }
      }

      // Update config if available
      if (data.config) {
        config.value = {
          enableEventStreaming: data.config.enable_event_streaming || false,
          enableWebsocket: data.config.enable_websocket || false,
          enableSse: data.config.enable_sse || false,
          nodeUrl: data.config.node_url || '',
          pollInterval: data.config.poll_interval
        }
      }

      lastUpdateTime.value = new Date()

    } catch (err) {
      console.error('Failed to fetch streaming status:', err)
      error.value = err instanceof Error ? err.message : 'Unknown error'

      // Set error state
      status.value = {
        enabled: false,
        mode: 'unknown',
        status: 'failed',
        streaming: false,
        healthy: false
      }
    } finally {
      isLoading.value = false
    }
  }

  async function fetchHealth() {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:9122/api/v1'
      const response = await fetch(`${apiUrl}/streaming/health`)

      if (!response.ok) {
        throw new Error(`Failed to fetch streaming health: ${response.statusText}`)
      }

      const data = await response.json()

      // Update health status
      status.value.healthy = data.healthy || false
      status.value.status = data.connection?.status || status.value.status
      status.value.streaming = data.connection?.streaming || false

      return data

    } catch (err) {
      console.error('Failed to fetch streaming health:', err)
      status.value.healthy = false
      throw err
    }
  }

  async function fetchMetrics() {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:9122/api/v1'
      const response = await fetch(`${apiUrl}/streaming/metrics`)

      if (!response.ok) {
        throw new Error(`Failed to fetch streaming metrics: ${response.statusText}`)
      }

      const data = await response.json()

      if (data.enabled) {
        metrics.value = {
          totalEventsProcessed: data.total_events_processed || 0,
          eventsByType: data.events_by_type || {},
          lastEventTime: data.last_event_time || null,
          eventsPerMinute: 0,
          connectionUptime: 0
        }
      }

      return data

    } catch (err) {
      console.error('Failed to fetch streaming metrics:', err)
      throw err
    }
  }

  function startPeriodicUpdate(intervalMs: number = 10000) {
    // Fetch immediately
    fetchStatus()

    // Then fetch periodically
    const intervalId = setInterval(() => {
      fetchStatus()
    }, intervalMs)

    // Return cleanup function
    return () => clearInterval(intervalId)
  }

  function reset() {
    status.value = {
      enabled: false,
      mode: 'unknown',
      status: 'unknown',
      streaming: false,
      healthy: false
    }

    metrics.value = {
      totalEventsProcessed: 0,
      eventsByType: {},
      lastEventTime: null,
      eventsPerMinute: 0,
      connectionUptime: 0
    }

    config.value = {
      enableEventStreaming: false,
      enableWebsocket: false,
      enableSse: false,
      nodeUrl: ''
    }

    lastUpdateTime.value = null
    error.value = null
  }

  return {
    // State
    status,
    metrics,
    config,
    lastUpdateTime,
    isLoading,
    error,

    // Computed
    isRealtime,
    connectionStatusText,
    connectionStatusColor,
    mostActiveEventTypes,

    // Actions
    fetchStatus,
    fetchHealth,
    fetchMetrics,
    startPeriodicUpdate,
    reset
  }
})
